import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ShieldCheck, MapPin, Star, Quote, Phone, Route, MessageCircle, Check, Plane, Hotel } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import VehicleCard from "@/components/VehicleCard";
import { api } from "@/lib/api";
import { CONTACT, waLink } from "@/lib/contact";
import { FAQS } from "@/lib/faq";
import { FadeUp, MaskedLine, EASE } from "@/components/motion/Reveal";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

const CATEGORY_TABS = [
  { id: "all", label: "All Vehicles" },
  { id: "car", label: "Car Rental" },
  { id: "suv_van", label: "SUV & Van Rental" },
  { id: "motorbike", label: "Motorbike Rental" },
];

const WHY = [
  "Local Palawan vehicle rental company",
  "Cars, SUVs, vans and motorbikes",
  "Airport delivery",
  "Hotel delivery",
  "El Nido pickup and drop-off",
  "Self-drive rentals",
  "Easy online reservation",
  "Secure online payment",
  "Clear rental requirements",
  "DTI registered · Mayor's Permit",
];

const PICKUP_POINTS = [
  { icon: Plane, label: "Puerto Princesa Airport" },
  { icon: MapPin, label: "Puerto Princesa City" },
  { icon: Hotel, label: "Hotel delivery" },
  { icon: Route, label: "El Nido pickup" },
  { icon: Route, label: "El Nido drop-off" },
  { icon: ArrowRight, label: "Puerto Princesa → El Nido" },
  { icon: ArrowRight, label: "El Nido → Puerto Princesa" },
];

const reviews = [
  { name: "Maria Santos", role: "Manila → Palawan, March 2025", quote: "Booked the Innova for 5 days with my family. Spotless car, super smooth process, and the team even delivered to our hotel. Will definitely book again on our next trip." },
  { name: "James O.", role: "Australia, Honeymoon", quote: "Rented an Aerox 155 for two weeks. Bike was brand new and in perfect shape. Pearly was super responsive — the whole experience felt premium and personal." },
  { name: "Cebu Travel Group", role: "Group of 12, August 2024", quote: "We needed two vans for a corporate trip. The Hi Ace Deluxe was immaculate. Best rental experience we've had anywhere in the Philippines." },
  { name: "Ken & Aya Tanaka", role: "Japan, February 2025", quote: "Fortuner was in showroom condition. The El Nido drop-off option saved our itinerary — we drove up, they picked it up. Flawless service." },
  { name: "Liza Ramirez", role: "Davao, May 2025", quote: "Rented the Ertiga Hybrid for a week and got a discount for the long rental. Pickup in front of Motortrade was so easy to find. Highly recommended!" },
  { name: "Mark D.", role: "USA, Underground River Tour", quote: "Click 125 was perfect for zipping around the city and to Sabang. Helmets included, zero hassle, fair prices. These guys run a tight ship." },
];

function ReviewsMarquee() {
  const doubled = [...reviews, ...reviews];
  return (
    <div className="relative mt-12 overflow-hidden" data-testid="reviews-marquee">
      <div className="absolute inset-y-0 left-0 w-24 bg-gradient-to-r from-ink-900 to-transparent z-10 pointer-events-none" />
      <div className="absolute inset-y-0 right-0 w-24 bg-gradient-to-l from-ink-900 to-transparent z-10 pointer-events-none" />
      <motion.div className="flex gap-6 w-max" animate={{ x: ["0%", "-50%"] }} transition={{ duration: 55, repeat: Infinity, ease: "linear" }}>
        {doubled.map((t, i) => (
          <div key={i} className="glass-card rounded-2xl p-7 relative w-[320px] sm:w-[400px] flex-shrink-0">
            <Quote className="w-7 h-7 text-gold-500/40 mb-4" />
            <p className="text-foreground/80 leading-relaxed italic font-display text-lg">"{t.quote}"</p>
            <div className="mt-6 flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-royal-700 to-gold-600 flex items-center justify-center text-white font-display text-lg">{t.name[0]}</div>
              <div>
                <p className="font-sans-display text-sm text-gold-200">{t.name}</p>
                <p className="text-[11px] text-foreground/50">{t.role}</p>
              </div>
              <div className="ml-auto flex gap-0.5">
                {Array.from({ length: 5 }).map((_, idx) => <Star key={idx} className="w-3.5 h-3.5 fill-gold-400 text-gold-400" />)}
              </div>
            </div>
          </div>
        ))}
      </motion.div>
    </div>
  );
}

export default function Home() {
  const [vehicles, setVehicles] = useState([]);
  const [tab, setTab] = useState("all");

  useEffect(() => {
    document.title = "PB-Lux Vehicle Rental — Car & Motorbike Rental in Puerto Princesa, Palawan";
    api.get("/vehicles").then((r) => setVehicles(r.data || [])).catch(() => {});
  }, []);

  const filtered = vehicles.filter((v) => {
    if (tab === "car") return v.type === "car" && ["hatchback", "sedan"].includes(v.category);
    if (tab === "suv_van") return ["suv", "mpv", "pickup", "van"].includes(v.category);
    if (tab === "motorbike") return v.type === "motorbike";
    return true;
  });
  const shown = tab === "all" ? filtered.slice(0, 9) : filtered;

  return (
    <div className="min-h-screen relative">
      <Navbar />

      {/* HERO — compact, conversion-first */}
      <section className="relative hero-bg noise overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0">
          <img src="https://images.unsplash.com/photo-1469854523086-cc02fe5d8800?w=2400&q=80" alt="Palawan coastal road self-drive rental" className="w-full h-full object-cover opacity-25" />
          <div className="absolute inset-0 bg-gradient-to-b from-ink-900/70 via-ink-900/80 to-ink-900" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 pt-32 pb-16 text-center">
          <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: EASE }} className="kicker justify-center" data-testid="hero-kicker">
            Puerto Princesa · Palawan
          </motion.p>
          <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl mt-5 leading-[1.02] tracking-tight">
            <MaskedLine>PB-LUX <em className="not-italic text-gold-gradient">VEHICLE RENTAL</em></MaskedLine>
          </h1>
          <motion.p initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.3, ease: EASE }}
            className="mt-4 font-sans-display uppercase tracking-[0.25em] text-sm sm:text-base text-foreground/75">
            Car & Motorbike Rental in Puerto Princesa, Palawan
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.45, ease: EASE }}
            className="mt-8 flex flex-wrap items-center justify-center gap-4">
            <a href="#fleet" className="btn-gold !text-sm !px-8 !py-4" data-testid="hero-view-vehicles">
              View Vehicles <ArrowRight className="w-4 h-4" />
            </a>
            <a href={waLink("Hi PB-Lux! I'd like to ask about renting a vehicle in Palawan.")} target="_blank" rel="noreferrer"
              className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-green-600 hover:bg-green-500 text-white font-sans-display text-sm uppercase tracking-wider transition" data-testid="hero-whatsapp">
              <MessageCircle className="w-4 h-4" /> WhatsApp Us
            </a>
          </motion.div>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.7 }} className="mt-7 flex flex-wrap items-center justify-center gap-3 text-[11px] text-foreground/60">
            <span className="inline-flex items-center gap-1.5"><ShieldCheck className="w-3.5 h-3.5 text-green-400" /> DTI Registered · Mayor's Permit</span>
            <span>·</span>
            <span>El Nido Pickup & Drop-off Available</span>
            <span>·</span>
            <span>Airport & Hotel Delivery</span>
          </motion.div>
        </div>
      </section>

      {/* FLEET — vehicles visible immediately */}
      <section id="fleet" className="relative section-bg-dark py-16 sm:py-20" data-testid="home-fleet-section">
        <div className="max-w-7xl mx-auto px-6">
          <FadeUp className="text-center">
            <p className="kicker justify-center">Our Fleet · Real Units, Real Prices</p>
            <h2 className="font-display text-4xl sm:text-5xl mt-4">Choose Your Ride</h2>
            <div className="gold-bar mt-5" />
          </FadeUp>
          <div className="mt-8 flex flex-wrap justify-center gap-2" data-testid="fleet-tabs">
            {CATEGORY_TABS.map((t) => (
              <button key={t.id} onClick={() => setTab(t.id)} data-testid={`fleet-tab-${t.id}`}
                className={`px-5 py-2.5 rounded-full text-xs sm:text-sm font-sans-display uppercase tracking-wider border transition ${tab === t.id ? "bg-gold-500/20 text-gold-200 border-gold-500/50" : "text-foreground/60 border-gold-500/15 hover:border-gold-500/40 hover:text-gold-200"}`}>
                {t.label}
              </button>
            ))}
          </div>
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="home-fleet-grid">
            {shown.map((v, i) => (
              <FadeUp key={v.id} delay={(i % 3) * 0.06}>
                <VehicleCard vehicle={v} index={i} />
              </FadeUp>
            ))}
          </div>
          {shown.length === 0 && <p className="text-center text-foreground/40 py-14">Loading fleet…</p>}
          <FadeUp className="text-center mt-10">
            <Link to="/fleet" className="btn-outline-gold" data-testid="view-all-fleet">
              View Full Fleet <ArrowRight className="w-4 h-4" />
            </Link>
          </FadeUp>
        </div>
      </section>

      {/* PALAWAN PICKUP & DROP-OFF */}
      <section className="relative section-bg-deep py-16 sm:py-20" data-testid="pickup-dropoff-section">
        <div className="max-w-6xl mx-auto px-6 grid lg:grid-cols-2 gap-10 items-center">
          <FadeUp>
            <p className="kicker">Palawan Pickup & Drop-off</p>
            <h2 className="font-display text-4xl sm:text-5xl mt-4 leading-tight">El Nido Pickup <span className="text-gold-gradient">&</span> Drop-off Available</h2>
            <p className="mt-5 text-foreground/65 leading-relaxed text-sm">
              Start or end your rental where it suits your itinerary — all four combinations are supported:
              Puerto Princesa → Puerto Princesa, Puerto Princesa → El Nido, El Nido → Puerto Princesa, and El Nido → El Nido.
              Your <span className="text-gold-300">daily rate never changes</span> by location — El Nido transfer fees are shown separately and transparently at booking.
            </p>
            <p className="mt-4 text-[11px] text-foreground/45">
              Pickup and drop-off availability depends on the vehicle and booking schedule. Please select your preferred locations during booking.
            </p>
          </FadeUp>
          <FadeUp delay={0.1}>
            <div className="glass-card rounded-2xl p-7">
              <ul className="space-y-3.5">
                {PICKUP_POINTS.map((p) => (
                  <li key={p.label} className="flex items-center gap-3 text-sm text-foreground/80">
                    <span className="w-8 h-8 rounded-full bg-gold-500/10 border border-gold-500/30 flex items-center justify-center flex-shrink-0">
                      <Check className="w-4 h-4 text-green-400" />
                    </span>
                    {p.label}
                  </li>
                ))}
              </ul>
            </div>
          </FadeUp>
        </div>
      </section>

      {/* WHY CHOOSE PB-LUX */}
      <section className="relative section-bg-dark py-16 sm:py-20" data-testid="why-section">
        <div className="max-w-6xl mx-auto px-6">
          <FadeUp className="text-center">
            <p className="kicker justify-center">Trusted. Local. Transparent.</p>
            <h2 className="font-display text-4xl sm:text-5xl mt-4">Why Choose PB-Lux?</h2>
            <div className="gold-bar mt-5" />
          </FadeUp>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-5 gap-3">
            {WHY.map((w, i) => (
              <FadeUp key={w} delay={(i % 5) * 0.05}>
                <div className="glass-card rounded-xl px-4 py-5 h-full flex items-start gap-3 hover:border-gold-500/40 transition-colors">
                  <Check className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-foreground/80">{w}</p>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* REVIEWS */}
      <section className="relative section-bg-deep py-16 sm:py-20 overflow-hidden" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto px-6">
          <FadeUp className="text-center max-w-2xl mx-auto">
            <p className="kicker justify-center">Travelers' Words</p>
            <h2 className="font-display text-4xl sm:text-5xl mt-4">Customer Reviews</h2>
            <div className="gold-bar mt-5" />
            <div className="mt-6 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gold-500/10 border border-gold-500/30 text-sm text-gold-200" data-testid="reviews-rating-badge">
              <span className="flex gap-0.5">{Array.from({ length: 5 }).map((_, i) => <Star key={i} className="w-4 h-4 fill-gold-400 text-gold-400" />)}</span>
              Rated by our guests on Facebook & Google
            </div>
          </FadeUp>
        </div>
        <ReviewsMarquee />
        <FadeUp className="text-center mt-8">
          <a href={CONTACT.facebookUrl} target="_blank" rel="noreferrer" className="btn-outline-gold !text-xs" data-testid="more-reviews-link">
            View More Reviews on Facebook <ArrowRight className="w-3.5 h-3.5" />
          </a>
        </FadeUp>
      </section>

      {/* FAQ */}
      <section className="relative section-bg-dark py-16 sm:py-20" data-testid="faq-section">
        <div className="max-w-3xl mx-auto px-6">
          <FadeUp className="text-center">
            <p className="kicker justify-center">Good to Know</p>
            <h2 className="font-display text-4xl sm:text-5xl mt-4">Frequently Asked Questions</h2>
            <div className="gold-bar mt-5" />
          </FadeUp>
          <FadeUp delay={0.1} className="mt-10">
            <Accordion type="single" collapsible className="space-y-3">
              {FAQS.map((f, i) => (
                <AccordionItem key={i} value={`faq-${i}`} className="glass-card rounded-xl px-5 border-gold-500/15" data-testid={`faq-item-${i}`}>
                  <AccordionTrigger className="text-left text-sm font-sans-display text-gold-100 hover:text-gold-300 hover:no-underline">{f.q}</AccordionTrigger>
                  <AccordionContent className="text-sm text-foreground/65 leading-relaxed">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </FadeUp>
        </div>
      </section>

      {/* FIND US — MAP */}
      <section className="relative section-bg-deep py-16 sm:py-20" data-testid="find-us-section">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-12 gap-10 items-center">
          <FadeUp className="lg:col-span-5">
            <p className="kicker">Visit the Shop</p>
            <h2 className="font-display text-4xl sm:text-5xl mt-4">Find Us in Puerto Princesa</h2>
            <div className="gold-bar mt-5 !mx-0" />
            <div className="mt-8 space-y-4 text-sm text-foreground/70">
              <p className="flex items-start gap-3">
                <MapPin className="w-4 h-4 text-gold-400 mt-1 flex-shrink-0" />
                <span><span className="text-gold-200 font-medium">PB-Lux Vehicle Rental</span><br />In front of Motortrade, San Miguel,<br />Puerto Princesa City, Palawan</span>
              </p>
              <p className="flex items-center gap-3">
                <Phone className="w-4 h-4 text-gold-400 flex-shrink-0" />
                <a href={`tel:${CONTACT.phoneRaw}`} className="hover:text-gold-300 transition">{CONTACT.phone}</a>
              </p>
            </div>
            <a href="https://maps.google.com/?q=Motortrade,+San+Miguel,+Puerto+Princesa,+Palawan" target="_blank" rel="noreferrer" className="btn-outline-gold mt-8 inline-flex" data-testid="open-in-maps">
              Open in Google Maps <ArrowRight className="w-4 h-4" />
            </a>
          </FadeUp>
          <FadeUp delay={0.12} className="lg:col-span-7">
            <div className="relative rounded-3xl overflow-hidden border border-gold-500/25 shadow-2xl shadow-royal-900/50">
              <iframe
                title="PB-Lux Vehicle Rental — In front of Motortrade, San Miguel, Puerto Princesa"
                src="https://maps.google.com/maps?q=Motortrade%2C%20San%20Miguel%2C%20Puerto%20Princesa%2C%20Palawan&t=&z=16&ie=UTF8&iwloc=&output=embed"
                className="w-full h-[380px] grayscale-[35%] contrast-[1.05]"
                style={{ border: 0 }}
                loading="lazy"
                allowFullScreen
                referrerPolicy="no-referrer-when-downgrade"
                data-testid="google-map-embed"
              />
            </div>
          </FadeUp>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-20 overflow-hidden" data-testid="cta-section">
        <div className="absolute inset-0">
          <img src="https://images.unsplash.com/photo-1502780402662-acc01917e60c?w=2400&q=80" alt="Palawan road trip" className="w-full h-full object-cover opacity-25" />
          <div className="absolute inset-0 bg-gradient-to-r from-ink-900 via-ink-900/85 to-royal-950/80" />
        </div>
        <div className="relative max-w-4xl mx-auto px-6 text-center">
          <h2 className="font-display text-4xl sm:text-6xl leading-tight">
            <MaskedLine>See it. Reserve it.</MaskedLine>
            <MaskedLine delay={0.15}><span className="text-gold-gradient">Drive Palawan.</span></MaskedLine>
          </h2>
          <FadeUp delay={0.3}>
            <p className="mt-5 text-foreground/70 max-w-xl mx-auto text-sm">
              Reserve online in minutes — pay just one day's rental to lock in your unit. The rest is due on pickup.
            </p>
            <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
              <a href="#fleet" className="btn-gold" data-testid="cta-book-now">View Vehicles <ArrowRight className="w-4 h-4" /></a>
              <a href={waLink("Hi PB-Lux! I'd like to ask about renting a vehicle in Palawan.")} target="_blank" rel="noreferrer" className="btn-outline-gold">
                <MessageCircle className="w-4 h-4" /> WhatsApp Us
              </a>
            </div>
          </FadeUp>
        </div>
      </section>

      <Footer />
    </div>
  );
}
